import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@lib/components/Modal/Modal';
import Form, { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import formStyles from '@lib/assets/styles/Form.module.scss';
import useModalManagement from '@lib/hooks/useModalManagement';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import AssetsModalTableMultiselect from 'components/AssetsModalTableMultiselect/AssetsModalTableMultiselect';
import SiteAreasModalTableMultiselect from 'components/SiteAreasModalTableMultiselect/SiteAreasModalTableMultiselect';
import TasksModalTableMultiselect from 'components/TasksModalTableMultiselect/TasksModalTableMultiselect';
import useDocumentBulkEditActions from '../utils/useDocumentBulkEditActions';
import DocumentBulkEditActions from './DocumentBulkEditActions';

const SELECT_EQUIPMENT_MODAL = 'bulk-edit-select-equipment';
const SELECT_AREAS_MODAL = 'bulk-edit-select-areas';
const SELECT_TASKS_MODAL = 'bulk-edit-select-tasks';
const BULK_EDIT_MODALS = [
  SELECT_EQUIPMENT_MODAL,
  SELECT_AREAS_MODAL,
  SELECT_TASKS_MODAL,
];

interface DocumentBulkEditModalProps {
  ids: string[];
  isOpen: boolean;
  onClose: () => void;
  onCompleted: () => void;
}

export default function DocumentBulkEditModal({
  ids,
  isOpen,
  onClose,
  onCompleted,
}: DocumentBulkEditModalProps) {
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);
  const useModalManagementResult = useModalManagement(BULK_EDIT_MODALS);

  const [equipmentVisibilityMap, setEquipmentVisibilityMap] = useState<
    Record<string, boolean>
  >({});
  const [areasVisibilityMap, setAreasVisibilityMap] = useState<
    Record<string, boolean>
  >({});

  const openEquipmentModal = useCallback(() => {
    useModalManagementResult.openModal(SELECT_EQUIPMENT_MODAL);
  }, [useModalManagementResult]);

  const openAreasModal = useCallback(() => {
    useModalManagementResult.openModal(SELECT_AREAS_MODAL);
  }, [useModalManagementResult]);

  const openTasksModal = useCallback(() => {
    useModalManagementResult.openModal(SELECT_TASKS_MODAL);
  }, [useModalManagementResult]);

  const onCloseEquipmentModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_EQUIPMENT_MODAL);
  }, [useModalManagementResult]);

  const onCloseAreasModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_AREAS_MODAL);
  }, [useModalManagementResult]);

  const onCloseTasksModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_TASKS_MODAL);
  }, [useModalManagementResult]);

  const onEquipmentSelect = useCallback(
    (values: string[], visibilityMap?: Record<string, boolean>) => {
      if (formRef.current) {
        formRef.current.setValue(
          DocumentFormFieldNames.Equipment,
          values.length
            ? values.map((id) => {
                const visibility = visibilityMap?.[id];
                return visibility === undefined
                  ? { value: id }
                  : { value: id, visibleViaQr: visibility };
              })
            : [],
          { shouldDirty: true, shouldTouch: true, shouldValidate: true },
        );
      }
    },
    [],
  );

  const onAreasSelect = useCallback(
    (values: string[], visibilityMap?: Record<string, boolean>) => {
      if (formRef.current) {
        formRef.current.setValue(
          DocumentFormFieldNames.Areas,
          values.length
            ? values.map((id) => {
                const visibility = visibilityMap?.[id];
                return visibility === undefined
                  ? { value: id }
                  : { value: id, visibleViaQr: visibility };
              })
            : [],
          { shouldDirty: true, shouldTouch: true, shouldValidate: true },
        );
      }
    },
    [],
  );

  const onTasksSelect = useCallback((values: string[]) => {
    if (formRef.current) {
      formRef.current.setValue(
        DocumentFormFieldNames.Tasks,
        values.length ? values.map((id) => ({ value: id })) : [],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  }, []);

  const { formFields, loading, onSubmit } = useDocumentBulkEditActions({
    ids,
    onCompleted,
    openEquipmentModal,
    openAreasModal,
    openTasksModal,
  });

  const isEquipmentModalOpen = useModalManagementResult.isModalOpened(
    SELECT_EQUIPMENT_MODAL,
  );
  const isAreasModalOpen =
    useModalManagementResult.isModalOpened(SELECT_AREAS_MODAL);
  const isTasksModalOpen =
    useModalManagementResult.isModalOpened(SELECT_TASKS_MODAL);

  return (
    <>
      <Modal isOpen={isOpen} caption={t('bulk-edit')} onClose={onClose}>
        <Form
          formRef={formRef}
          fields={formFields}
          onSubmit={onSubmit}
          defaultValues={{}}
          actionsComponent={DocumentBulkEditActions}
          actionsComponentProps={{
            onCancel: onClose,
          }}
          loading={loading}
          formClassName={formStyles.form}
        />
      </Modal>
      <AssetsModalTableMultiselect
        isOpen={isEquipmentModalOpen}
        onClose={onCloseEquipmentModal}
        onSelect={onEquipmentSelect}
        formRef={formRef}
        fieldName={DocumentFormFieldNames.Equipment}
        visibleViaQr={{
          initialVisibilityMap: equipmentVisibilityMap,
          onVisibilityMapChange: setEquipmentVisibilityMap,
        }}
      />
      <SiteAreasModalTableMultiselect
        isOpen={isAreasModalOpen}
        onClose={onCloseAreasModal}
        onSelect={onAreasSelect}
        formRef={formRef}
        fieldName={DocumentFormFieldNames.Areas}
        visibleViaQr={{
          initialVisibilityMap: areasVisibilityMap,
          onVisibilityMapChange: setAreasVisibilityMap,
        }}
      />
      <TasksModalTableMultiselect
        isOpen={isTasksModalOpen}
        onClose={onCloseTasksModal}
        onSelect={onTasksSelect}
        formRef={formRef}
        fieldName={DocumentFormFieldNames.Tasks}
      />
    </>
  );
}
